import React, {useEffect, useCallback, useState} from 'react';
import {useParams} from 'react-router-dom';
import t from 'babel-plugin-yate/macro';
import {isWithinInterval, setHours, getHours, getDay} from 'date-fns';
import {useDispatch} from 'react-redux';

import Modal from '../components/Modal';
import Text from '../components/uiElem/Text';

import circleCheck from '../assets/circle-check.svg';
import circleInfo from '../assets/circle-info.svg';
import Button from '../components/Button';
import {getApplicationRedirect} from '../store/application';
import ValidationError from '../components/Form/ValidationError';

const getWorkingTimeMessage = () => {
  const currentDate = new Date();

  // weekend or friday, after 17:00
  if (
    [6, 7].includes(getDay(currentDate)) ||
    (getDay(currentDate) === 5 && getHours(currentDate) >= 17)
  ) {
    return t`Cererea dumneavoastra se afla in analiza. Vom reveni cu raspuns incepand cu urmatoarea zi lucratoare. Va multumim.`;
  }

  // working time
  if (
    isWithinInterval(currentDate, {
      start: setHours(currentDate, 8).setMinutes(59),
      end: setHours(currentDate, 17).setMinutes(0),
    })
  ) {
    return t`Cererea dumneavoastra se afla in analiza. Ne straduim sa revenim cu raspuns in aproximativ o ora. Va multumim.`;
  }

  return t`Cererea dumneavoastra se afla in analiza. Vom reveni cu raspuns incepand cu ora 09:00 dimineata. Va multumim.`;
};

const infoMsg = {
  newHeader: t`Request for analysis`,
  newMessage: t`Your request is under analysis.`,
  newIconSrc: null,
};

const getOnlineModalInfoMessages = ({
  status,
  has_redirect,
  rejected_for_different_cnp,
  has_external_refinancing,
  has_refinancing_with_extra_amount,
  payment,
}) => {
  switch (status) {
    case 'awaiting_cb':
      infoMsg.newMessage = t`Please wait for the answer from the Credit Bureau.`;
      break;
    case 'awaiting_kyc':
    case 'ocr':
    case 'selfie':
      infoMsg.newHeader = t`Data processing`;
      infoMsg.newMessage = t`Please wait for your data to be processed.`;
      break;
    case 'canceled':
      infoMsg.newHeader = t`Application canceled`;
      infoMsg.newMessage = t`Sorry, your request was canceled.`;
      infoMsg.newIconSrc = circleInfo;
      break;
    case 'rejected':
      infoMsg.newHeader = t`Application rejected`;
      infoMsg.newMessage = t`Sorry, your request was rejected.`;
      infoMsg.newIconSrc = circleInfo;

      if (rejected_for_different_cnp) {
        infoMsg.newMessage += '<br /><br />' + t`Rejected - wrong CNP.`;
      }

      // infoMsg.newMessage += '<br /><br />' + t`Contact us by email.`;

      if (has_redirect) {
        infoMsg.newMessage = t`Sorry, your request was rejected but you can continue with TBI.`;
      }
      break;
    case 'expired':
      infoMsg.newHeader = t`Application expired`;
      infoMsg.newMessage = t`Sorry, your request was expired.`;
      infoMsg.newIconSrc = circleInfo;
      break;
    case 'signed':
    case 'done':
      infoMsg.newHeader = t`Congratulations!`;
      infoMsg.newMessage = t`The credit agreement was concluded successfully. In the shortest time, you will receive by email the confirmation of the conclusion of the contract.`;
      infoMsg.newIconSrc = circleCheck;

      if (
        has_external_refinancing &&
        has_refinancing_with_extra_amount &&
        payment === 'iban'
      ) {
        infoMsg.newMessage +=
          '<br /><br />' +
          t`Pentru a putea procesa disbursarea sumei suplimentare, te rugam sa ne transmiti adresa de sold 0 emisa de creditorul refinantat, in termen de 30 de zile de la semnarea contractului, la adresa de email <a class="text-14 leading-tight text-blue underline hover:no-underline cursor-pointer" href="mailto:analizacredite@credex-ifn.ro">analizacredite@credex-ifn.ro</a>`;
        break;
      }

      if (has_external_refinancing) {
        infoMsg.newMessage +=
          '<br /><br />' +
          t`Te rugam sa trimiti adresa de sold 0 emisa de creditorul refinantat, in termen de 30 de zile de la semnarea contractului catre adresa de email <a class="text-14 leading-tight text-blue underline hover:no-underline cursor-pointer" href="mailto:analizacredite@credex-ifn.ro">analizacredite@credex-ifn.ro</a>`;
        break;
      }

      break;
    case 'manual_analysis':
    case 'risk_analysis':
    case 'risk_manual_analysis':
    case 'risk_validated':
      infoMsg.newMessage = getWorkingTimeMessage();
      break;
    case 'duplicate_contract':
      infoMsg.newHeader = t`Duplicated contract`;
      infoMsg.newMessage = t`Sorry, your request is Duplicated.`;
      infoMsg.newMessage += '<br /><br />' + t`Contact us by email.`;
      infoMsg.newIconSrc = circleInfo;
      break;
    default:
  }

  return infoMsg;
};

const getTransactionInfoMessage = ({
  status,
  transactionErrorMessage,
  in_progress,
  in_progress_message,
  tx_type,
}) => {
  if (in_progress_message) {
    infoMsg.newHeader = '';
    infoMsg.newMessage = in_progress_message;
    infoMsg.newIconSrc = null;

    return infoMsg;
  }

  if (transactionErrorMessage) {
    infoMsg.newHeader = '';
    infoMsg.newMessage = transactionErrorMessage.message.replace(
      `{tx_link}`,
      `<a class="text-14 leading-tight text-blue underline hover:no-underline cursor-pointer" href="${transactionErrorMessage.tx_link}">${transactionErrorMessage.tx_link}</a>`,
    );
    infoMsg.newIconSrc = circleInfo;

    return infoMsg;
  }

  if (!in_progress) {
    infoMsg.newIconSrc = circleInfo;

    switch (status) {
      case 'cancelled':
        infoMsg.newHeader = t`Transaction canceled`;
        infoMsg.newMessage =
          tx_type === 5
            ? t`Transaction canceled message PNL`
            : t`Transaction canceled message CF`;
        break;
      case 'rejected':
        infoMsg.newHeader = t`Application rejected`;
        infoMsg.newMessage = t`Sorry, your request was rejected.`;
        infoMsg.newMessage += '<br /><br />' + t`Contact us by email.`;
        break;
      case 'expired':
        infoMsg.newHeader = t`Application expired`;
        infoMsg.newMessage = t`Sorry, your request was expired.`;
        break;
      case 'signed':
      case 'done':
        infoMsg.newHeader = t`Congratulations!`;
        infoMsg.newMessage =
          tx_type === 5
            ? t`Transaction success message PNL`
            : t`Transaction success message CF`;
        infoMsg.newIconSrc = circleCheck;
        break;
      default:
    }
  }

  return infoMsg;
};

function ModalInfo({data, id, appType, transactionErrorMessage}) {
  const dispatch = useDispatch();
  const {uuid} = useParams();
  const [header, setHeader] = useState();
  const [infoMessage, setInfoMessage] = useState();
  const [iconSrc, setIconSrc] = useState();
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const {
    status,
    to_be_redirected_to,
    has_redirect,
    rejected_for_different_cnp,
    has_external_refinancing,
    has_refinancing_with_extra_amount,
    payment,
    in_progress,
    in_progress_message,
    tx_type,
  } = data;

  const getRedirectLink = useCallback(async () => {
    setLoading(true);
    let redirectUrl = to_be_redirected_to;

    if (!redirectUrl) {
      const response = await dispatch(getApplicationRedirect(uuid));
      redirectUrl = response?.to_be_redirected_to;
    }

    if (redirectUrl) {
      window.location.href = redirectUrl;
    } else {
      setError(
        t`Ne pare rau, a intervenit o eroare la transmiterea comenzii catre urmatorul finantator.`,
      );
    }

    setLoading(false);
  }, [dispatch, to_be_redirected_to, uuid]);

  useEffect(() => {
    let objMsg = {}; // {newMessage, newHeader, newIconSrc}

    if (appType === 'online') {
      objMsg = getOnlineModalInfoMessages({
        status,
        has_redirect,
        rejected_for_different_cnp,
        has_external_refinancing,
        has_refinancing_with_extra_amount,
        payment,
      });
    }

    if (appType === 'transaction') {
      objMsg = getTransactionInfoMessage({
        status,
        transactionErrorMessage,
        in_progress,
        in_progress_message,
        tx_type,
      });
    }

    setInfoMessage(objMsg.newMessage);
    setHeader(objMsg.newHeader);
    setIconSrc(objMsg.newIconSrc);
  }, [
    status,
    has_redirect,
    rejected_for_different_cnp,
    has_external_refinancing,
    has_refinancing_with_extra_amount,
    payment,
    appType,
    transactionErrorMessage,
    in_progress,
    in_progress_message,
    tx_type,
  ]);

  return (
    <Modal id={id} width="max-w-sm" header={header} allowClose={false}>
      {iconSrc && (
        <div className="h-24 flex items-center justify-center text-36">
          <img src={iconSrc} alt="" className="h-16 w-16" />
        </div>
      )}

      {!iconSrc && appType === 'online' && (
        <div className="h-24 flex items-center justify-center text-36">
          <div className="spinner" />
        </div>
      )}

      <div className="py-4 px-8 text-center">
        <Text dangerouslySetInnerHTML={{__html: infoMessage}} />

        {status === 'rejected' && has_redirect && (
          <div className="mt-4">
            {loading ? (
              <div className="h-8 spinner" />
            ) : (
              <Button
                color={'green'}
                border={false}
                title={t`Continue`}
                onClick={getRedirectLink}
              />
            )}

            {error && <ValidationError value={error} />}
          </div>
        )}
      </div>
    </Modal>
  );
}

export default ModalInfo;
